import React from 'react';

import { 
  API, 
  Logger, 
  graphqlOperation,
}                     from 'aws-amplify';

import diff           from 'deep-diff';

import { 
  getExecution,
}                     from '../../../graphql/queries';

const logger = new Logger('useExecutions');

const listExecutions = `
  query ListExecutions(
    $filter: _ExecutionFilter
    $limit: Int
    $nextToken: String
  ) {
    listExecutions(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        ExecutionId
        WorkloadId
        WorkloadType
        ClientAccountId
        Configuration
        Description
        StartTime
        EndTime
        Feed
        Message
        Overrides
        Status
        Result
        SourceDatasetIds
        TargetDatasetId
        Inputs
        Outputs
        Metrics
        Tasks
      }
      nextToken
    }
  }
`;

const onMutateExecution = `
  subscription OnMutateExecution[args1] {
    onMutateExecution[args2] {
      mutationType
      clientAccountId
      executionId
      workloadId
      payload
    }
  }
`;

function resolveJson(rawExecution) {
  let resolvedExecution=rawExecution;
  if(resolvedExecution) {
    ["Configuration", "Overrides", "Inputs", "Outputs", "Metrics", "Tasks"].forEach( (elem) => {
      if(resolvedExecution.hasOwnProperty(elem)) {
        resolvedExecution[elem]=JSON.parse(resolvedExecution[elem]);
      }
    });
  }
  return resolvedExecution;
}

function executionsReducer(state, action) {
  switch (action.type) {
    case "SET":
      logger.info("SET: ", action.executions);
      return { 
        ...state,
        executionMap: new Map(action.executions.map( e => [e.ExecutionId, resolveJson(e)] )),
      };
    case "LOADING":
      return { 
        ...state,
        loading: action.status
      };
    case "ERROR":
      return { 
        ...state,
        error: action.message 
      };
    case "INSERT":
      let execution = JSON.parse(action.mutation.payload);
      execution['ClientAccountId']=action.mutation.clientAccountId;
      execution['WorkloadId']=action.mutation.workloadId;
      execution['ExecutionId']=action.mutation.executionId;
      logger.info(`INSERT ${action.mutation.executionId}`,execution);
      return { 
        ...state,
        executionMap: state.executionMap.set(execution.ExecutionId,execution), 
      };
    case "MODIFY":
      if(state.executionMap.has(action.mutation.executionId)) {
        const differences = JSON.parse(action.mutation.payload);
        const updatedExecution = JSON.parse(JSON.stringify(state.executionMap.get(action.mutation.executionId)));
        const updatedAttrs=[];
        differences.forEach((change) => {
           diff.applyChange(updatedExecution,null,change);
          updatedAttrs.push(change.path.join('.'));
        });
        logger.info(`MODIFY ${updatedExecution.ExecutionId}: ${updatedAttrs.join(', ')} `,differences);
        return { 
          ...state,
          executionMap: state.executionMap.set(updatedExecution.ExecutionId,updatedExecution),
        };
      } else {
        return state;
      }
    case "MUTATE":
      return executionsReducer(state, {
        type: action.mutation.mutationType,
        mutation: action.mutation,
      }); // INSERT, MODIFY or REMOVE
    case "REMOVE":
      logger.info('REMOVE',action.mutation.executionId);
      return { 
        ...state,
        executionMap: state.executionMap.delete(action.mutation.executionId) 
      };
    default:
      logger.error('UNKNOWN:',action.type);
      return state;
  }
}


function fmtQueryArgs(query, args1, args2) {
  const queryArgs1=query.replace('[args1]',args1);
  const queryArgs2=queryArgs1.replace('[args2]',args2);
  return queryArgs2;
}

export const useExecutions = (props) => {

  const [state, dispatch] = React.useReducer(executionsReducer, { 
    executions: [],
    executionMap: new Map(),
    loading: false,
    error: null
  });
  
  const { clientAccountId, workloadId, 
          result, status, subscribed } = props;

  React.useEffect(() => {
    let unmounted = false;
    dispatch({ type: 'LOADING', status: true});
    dispatch({ type: 'ERROR', message: null});
    let variables = null;
    if (clientAccountId || workloadId) {
      variables={
        filter: {},
        limit: 1000,
      };
      if (clientAccountId) {
        variables.filter['ClientAccountId']={ eq: clientAccountId };
      }
      if (workloadId) {
        variables.filter['WorkloadId']={ eq: workloadId };
      }
      if (result) {
        variables.filter['Result']={ eq: result };
      }
      if (status) {
        variables.filter['Status']={ eq: status };
      }
    }
    
    API.graphql(graphqlOperation(listExecutions,variables))
      .then(res => res.data.listExecutions?res.data.listExecutions.items:[])
      .then(items => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'SET', executions: items});
        }
      })
      .catch(err => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'ERROR', message: err});
        }
      });

    let subscription = null;
    const mutationType = (subscribed&&['INSERT', 'MODIFY', 'REMOVE'].indexOf(subscribed) >= 0)?subscribed:null;

    let subscriptionVariables = null;
    let subscriptionArgs1 = "";
    let subscriptionArgs2 = "";
    let args1 = [];
    let args2 = [];
    if (mutationType || clientAccountId || workloadId) {
      subscriptionVariables={};
      if (mutationType) {
        subscriptionVariables['MutationType'] = mutationType;
        args1.push('$MutationType: ID');
        args2.push('mutationType: $MutationType');
      }
      if (clientAccountId) {
        subscriptionVariables['ClientAccountId'] = clientAccountId;
        args1.push('$ClientAccountId: ID');
        args2.push('clientAccountId: $ClientAccountId');
      }
      if (workloadId) {
        subscriptionVariables['WorkloadId'] = workloadId;
        args1.push('$WorkloadId: ID');
        args2.push('workloadId: $WorkloadId');
      }
      subscriptionArgs1="(" + args1.join(',') + ")";
      subscriptionArgs2="(" + args2.join(',') + ")";
    }
    
    if(subscribed) {
      const subscribeOperation=fmtQueryArgs(onMutateExecution,subscriptionArgs1,subscriptionArgs2);
      subscription=API.graphql(graphqlOperation(subscribeOperation,subscriptionVariables)).subscribe({
        next: data => {
          const {
            value: {
              data: { onMutateExecution }
            }
          } = data;
          
          dispatch({type: 'MUTATE', mutation: onMutateExecution});
        }
      });
    }

    return () => { 
      unmounted = true;
      if(subscription) {
        logger.info("Unsubscribing executions");
        subscription.unsubscribe();
      }
    };

  }, [clientAccountId, workloadId, result, status, subscribed]);

  return { 
    executions: [...state.executionMap.values()],
    loading: state.loading, 
    error: state.error
  };
  
};

function executionReducer(state, action) {
  switch (action.type) {
    case "SET":
      logger.info(`SET ${action.execution.ExecutionId}: `, action.execution);
      return { 
        ...state,
        execution: resolveJson(action.execution),
      };
    case "LOADING":
      return { 
        ...state,
        loading: action.status
      };
    case "ERROR":
      return { 
        ...state,
        error: action.message 
      };
    case "MODIFY":
      const differences=JSON.parse(action.mutation.payload);
      const updatedExecution=JSON.parse(JSON.stringify(state.execution));
      const updatedAttrs=[];
      differences.forEach((change) => {
         diff.applyChange(updatedExecution,null,change); 
         updatedAttrs.push(change.path.join('.'));
      });
      logger.info(`MODIFY ${updatedExecution.ExecutionId}: ${updatedAttrs.join(', ')} `,differences);
      return { 
        ...state,
        execution: updatedExecution,
      };
    default:
      logger.error('UNKNOWN:',action.type);
      return state;
  }
}

export const useExecution = (executionId, options={ subscribed: false }) => {

  const [state, dispatch] = React.useReducer(executionReducer, { 
    execution: null, 
    loading: false,
    error: null
  });
  
  const { clientAccountId, workloadId, subscribed } = options;
  
  React.useEffect(() => {
    let unmounted = false;
    dispatch({ type: 'LOADING', status: true});
    dispatch({ type: 'ERROR', message: null});
    const variables = {
      executionId: executionId
    };

    API.graphql(graphqlOperation(getExecution,variables))
      .then(res => res.data.getExecution)
      .then(item => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'SET', execution: item});
        }
      })
      .catch(err => {
        if(!unmounted) {
          dispatch({ type: 'LOADING', status: false});
          dispatch({ type: 'ERROR', message: err});
        }
      });

    let subscription = null;

    let subscriptionVariables = {ExecutionId: executionId};
    let args1 = ["$ExecutionId: ID"];
    let args2 = ["executionId: $ExecutionId"];
    if (clientAccountId) {
      subscriptionVariables['ClientAccountId'] = clientAccountId;
      args1.push('$ClientAccountId: ID');
      args2.push('clientAccountId: $ClientAccountId');
    }
    if (workloadId) {
      subscriptionVariables['WorkloadId'] = workloadId;
      args1.push('$WorkloadId: ID');
      args2.push('workloadId: $WorkloadId');
    }
    const subscriptionArgs1=args1.length?"(" + args1.join(',') + ")":"";
    const subscriptionArgs2=args2.length?"(" + args2.join(',') + ")":"";

    if(subscribed) {
      logger.info("Subscribing execution");
      const subscribeOperation=fmtQueryArgs(onMutateExecution,subscriptionArgs1,subscriptionArgs2);
      subscription=API.graphql(graphqlOperation(subscribeOperation,subscriptionVariables)).subscribe({
        next: data => {
          const {
            value: {
              data: { onMutateExecution }
            }
          } = data;
          dispatch({ type: 'MODIFY', mutation: onMutateExecution});
        }
      });
    }
    
    return () => { 
      unmounted = true;
      if(subscription) {
        logger.info("Unsubscribing execution");
        subscription.unsubscribe();
      }
    };

  }, [executionId, clientAccountId, workloadId, subscribed]);
  
  return { 
    execution: state.execution,
    loading: state.loading,
    error: state.error,
  };
};
